// src/pages/shelbula/workspaces/WorkspaceHistoryTab.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { getFirestore, collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { Loader, User, Clock, ListOrdered, ArrowUpCircle, ArrowDownCircle, ExternalLink, History, FileClock, RotateCcw, ChevronDown, CheckCircle, FilePlus2, FileEdit, FileX2, FileSymlink } from 'lucide-react';
import { authManager } from '@/managers/AuthManager';
import PlatformTag from './PlatformTag';
import { motion, AnimatePresence } from 'framer-motion';
import StatusDisplay from './StatusDisplay';
import HistoryActionsMenu from './HistoryActionsMenu';
import { getWorkspaceSubcollectionRef } from '@/utils/workspaceFirestore';

/* ========================
 * formatRelativeDate - Formats a timestamp for display with granularity
 * ========================*/
const formatRelativeDate = (dateInput) => {
    if (!dateInput) return null;
    let date;
    if (dateInput.toDate && typeof dateInput.toDate === 'function') date = dateInput.toDate();
    else if (dateInput._seconds) date = new Date(dateInput._seconds * 1000 + (dateInput._nanoseconds || 0) / 1000000);
    else if (dateInput instanceof Date) date = new Date(dateInput);
    else if (typeof dateInput === 'string' || typeof dateInput === 'number') date = new Date(dateInput);
    else return null;
    
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return `${diffInSeconds}s ago`;
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays <= 30) return `${diffInDays} days ago`;

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatTokenCount = (num) => {
    if (num === null || num === undefined) return '0';
    if (num < 1000) return num.toLocaleString();
    return `${(num / 1000).toFixed(1)}k`;
};

const formatDuration = (start, end) => {
    if (!start || !end) return null;
    let startDate, endDate;
    if (start.toDate) startDate = start.toDate();
    else if (start._seconds) startDate = new Date(start._seconds * 1000);
    else startDate = new Date(start);
    if (end.toDate) endDate = end.toDate();
    else if (end._seconds) endDate = new Date(end._seconds * 1000);
    else endDate = new Date(end);
    
    const seconds = Math.round((endDate.getTime() - startDate.getTime()) / 1000);
    if (seconds < 0) return null;
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m ${seconds % 60}s`;
};

// Inline Cat SVG component for auto commits
const CatIcon = ({ size = 12 }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-emerald-400"
    >
        <path d="M12 5c.67 0 1.35.09 2 .26 1.78-2 5.03-2.84 6.42-2.26 1.4.58-.42 7-.42 7 .57 1.07 1 2.24 1 3.44C21 17.9 16.97 21 12 21s-9-3.1-9-7.56c0-1.2.43-2.37 1-3.44 0 0-1.89-6.42-.5-7 1.39-.58 4.72.23 6.5 2.23A9.04 9.04 0 0 1 12 5Z"/>
        <path d="M8 14v.5" />
        <path d="M16 14v.5" />
        <path d="M11.25 16.25h1.5L12 17l-.75-.75Z" />
    </svg>
);

const CompactCommitCard = ({ item, workspace, onInitiateUndo, onInitiateRevertToPoint, onInitiateRestart, onInitiateCancel, onInitiateRollbackToPlan }) => {
    const isHumanCommit = item.commitMessage && (item.commitMessage.includes('[Human Commit]') || !item.commitMessage.includes('[Shelbula Auto Commit]'));
    const commitType = isHumanCommit ? 'Human' : 'Auto';
    const displayMessage = item.commitMessage ? item.commitMessage.replace(/^\[(Human Commit|Shelbula Auto Commit)\]\s*/, '') : 'Commit';

    return (
        <div className={`bg-surface rounded-md border transition-all duration-200 ${!item.isAncestor ? 'opacity-50 saturate-75' : 'hover:bg-surface-dark'}`} style={{ borderColor: `rgba(var(--border-color-rgb), ${!item.isAncestor ? '0.3' : '0.5'})` }}>
            <div className="px-3 py-2 flex items-center gap-3">
                <div className="flex items-center gap-2 flex-shrink-0">
                    <div className={`w-5 h-5 rounded flex items-center justify-center ${isHumanCommit ? 'bg-blue-500/10' : 'bg-emerald-500/10'}`}>
                        {isHumanCommit ? <User size={12} className="text-blue-400" /> : <CatIcon size={12} />}
                    </div>
                    <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${isHumanCommit ? 'bg-blue-500/10 text-blue-400' : 'bg-emerald-500/10 text-emerald-400'}`}>{commitType}</span>
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-[.7em] font-medium text-on-surface truncate" title={displayMessage}>{displayMessage}</p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-xs text-on-surface-dim">{formatRelativeDate(item.createdAt)}</span>
                    {item.commitHash && workspace.repoUrl && (
                        <a href={`${workspace.repoUrl}/commit/${item.commitHash}`} target="_blank" rel="noopener noreferrer" className="text-xs text-on-surface-dim hover:text-primary transition-colors flex items-center gap-1" title="View on GitHub">
                            <ExternalLink size={12}/>
                        </a>
                    )}
                    <HistoryActionsMenu item={item} workspace={workspace} onInitiateUndo={onInitiateUndo} onInitiateRevertToPoint={onInitiateRevertToPoint} onInitiateRestart={onInitiateRestart} onInitiateCancel={onInitiateCancel} onInitiateRollbackToPlan={onInitiateRollbackToPlan} isCompact={true} />
                </div>
            </div>
        </div>
    );
};

const AIPhaseCard = ({ item, workspace, onInitiateUndo, onInitiateRevertToPoint, onInitiateRestart, onInitiateCancel, onInitiateRollbackToPlan }) => {
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);
    const hasPlanSteps = item.plan?.deliverableBlueprints?.length > 0;
    
    const usageDetails = useMemo(() => {
        if (!item.tokenUsage?.agents) {
            return { prompt: 0, response: 0 };
        }
        const allAgents = Object.values(item.tokenUsage.agents);
        const prompt = allAgents.reduce((sum, agent) => sum + (agent.promptTokens || 0), 0);
        const response = allAgents.reduce((sum, agent) => sum + (agent.responseTokens || 0), 0);
        return { prompt, response };
    }, [item.tokenUsage]);

    return (
        <div className={`bg-surface rounded-lg border border-border-color transition-all duration-300 ${!item.isAncestor ? 'opacity-60 saturate-[0.8]' : 'hover:border-border-color-light'}`} data-active={item.isAncestor}>
            <div className="p-3">
                <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex items-start gap-2 flex-1 min-w-0">
                        <div className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0 bg-emerald-500/10 mt-0.5">
                            <CatIcon size={12} />
                        </div>
                        <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 flex-shrink-0 mt-0.5">Task</span>
                        <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-[.7em] text-on-surface leading-snug line-clamp-3" title={item.objective || 'AI Task'}>
                                {item.objective || 'AI Task'}
                            </h4>
                        </div>
                    </div>
                </div>

                <div className="flex items-center justify-between">
                     <div className="flex items-center gap-3 text-xs text-on-surface-dim">
                        {item.tokenUsage && (
                            <>
                                <span title="Input Tokens" className="flex items-center gap-1"><ArrowUpCircle size={12} className="text-blue-400"/> {formatTokenCount(usageDetails.prompt)}</span>
                                <span title="Output Tokens" className="flex items-center gap-1"><ArrowDownCircle size={12} className="text-emerald-400"/> {formatTokenCount(usageDetails.response)}</span>
                            </>
                        )}
                        {item.createdAt && item.lastModified && <span title="Duration" className="flex items-center gap-1"><Clock size={12}/> {formatDuration(item.createdAt, item.lastModified) || '< 1s'}</span>}
                        {hasPlanSteps && <span title="Steps" className="flex items-center gap-1"><ListOrdered size={12}/> {item.plan.deliverableBlueprints.length}</span>}
                     </div>
                     <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1">{(item.platformsUsedSorted || []).map(p => <PlatformTag key={p} platform={p} compact />)}</div>
                        <HistoryActionsMenu item={item} workspace={workspace} onInitiateUndo={onInitiateUndo} onInitiateRevertToPoint={onInitiateRevertToPoint} onInitiateRestart={onInitiateRestart} onInitiateCancel={onInitiateCancel} onInitiateRollbackToPlan={onInitiateRollbackToPlan} isCompact={true} />
                     </div>
                </div>

                <div className="flex items-center justify-between mt-2 pt-2 border-t" style={{ borderColor: 'rgba(var(--border-color-rgb), 0.5)' }}>
                    <span className="text-xs text-on-surface-dim">{formatRelativeDate(item.createdAt)}</span>
                    <StatusDisplay status={item.status} className="px-2 py-0.5 rounded bg-surface border border-border-color text-[.65rem] flex-shrink-0" />
                    <div className="flex items-center gap-3">
                        {hasPlanSteps && (
                             <button onClick={() => setIsDetailsOpen(!isDetailsOpen)} className="text-xs text-on-surface-dim hover:text-primary transition-colors flex items-center gap-1">
                                Details
                                <ChevronDown size={14} className={`transition-transform duration-200 ${isDetailsOpen ? 'rotate-180' : ''}`} />
                            </button>
                        )}
                        {item.endOfPhaseCommitHash && workspace.repoUrl && (
                            <a href={`${workspace.repoUrl}/commit/${item.endOfPhaseCommitHash}`} target="_blank" rel="noopener noreferrer" className="text-xs text-on-surface-dim hover:text-primary transition-colors flex items-center gap-1">
                                <ExternalLink size={12}/> View Commit
                            </a>
                        )}
                    </div>
                </div>
            </div>
            <AnimatePresence>
                {isDetailsOpen && hasPlanSteps && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden"
                    >
                        <div className="border-t border-border-color bg-black/10 p-3">
                            <h5 className="font-semibold text-xs text-on-surface mb-2">Execution Plan</h5>
                            <ul className="space-y-1.5">
                                {item.plan.deliverableBlueprints.map((step, i) => (
                                    <li key={i} className="flex items-start gap-2 text-xs text-on-surface-dim">
                                        <CheckCircle size={13} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                                        <span>{step.thought || step.description || "No description provided."}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

const MUTATION_ACTION_META = {
    create: { label: 'Created', icon: FilePlus2, color: 'text-green-400', bg: 'bg-green-500/10' },
    update: { label: 'Updated', icon: FileEdit, color: 'text-blue-400', bg: 'bg-blue-500/10' },
    delete: { label: 'Deleted', icon: FileX2, color: 'text-red-400', bg: 'bg-red-500/10' },
    rename: { label: 'Renamed', icon: FileSymlink, color: 'text-amber-400', bg: 'bg-amber-500/10' },
};

const FileMutationCard = ({ item }) => {
    const meta = MUTATION_ACTION_META[item.action] || MUTATION_ACTION_META.update;
    const Icon = meta.icon;
    const displayPath = item.action === 'rename'
        ? `${item.oldPath} → ${item.newPath}`
        : (item.filePath || 'unknown');

    return (
        <div className="bg-surface rounded-md border transition-all duration-200 hover:bg-surface-dark" style={{ borderColor: 'rgba(var(--border-color-rgb), 0.5)' }}>
            <div className="px-3 py-2 flex items-center gap-3">
                <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${meta.bg}`}>
                    <Icon size={12} className={meta.color} />
                </div>
                <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${meta.bg} ${meta.color}`}>{meta.label}</span>
                <div className="flex-1 min-w-0">
                    <p className="text-[.7em] font-medium text-on-surface truncate" title={displayPath}>{displayPath}</p>
                </div>
                <span className="text-xs text-on-surface-dim flex-shrink-0">{formatRelativeDate(item.createdAt)}</span>
            </div>
        </div>
    );
};

const HistoryItemCard = ({ item, workspace, onInitiateUndo, onInitiateRevertToPoint, onInitiateRestart, onInitiateCancel, onInitiateRollbackToPlan }) => {
    const isAiPhase = item.type === 'AI_PHASE';
    const isFileMutation = item.type === 'FILE_MUTATION';
    
    if (isAiPhase) {
        return <AIPhaseCard item={item} workspace={workspace} onInitiateUndo={onInitiateUndo} onInitiateRevertToPoint={onInitiateRevertToPoint} onInitiateRestart={onInitiateRestart} onInitiateCancel={onInitiateCancel} onInitiateRollbackToPlan={onInitiateRollbackToPlan} />;
    } else if (isFileMutation) {
        return <FileMutationCard item={item} />;
    } else {
        return <CompactCommitCard item={item} workspace={workspace} onInitiateUndo={onInitiateUndo} onInitiateRevertToPoint={onInitiateRevertToPoint} onInitiateRestart={onInitiateRestart} onInitiateCancel={onInitiateCancel} onInitiateRollbackToPlan={onInitiateRollbackToPlan} />;
    }
};

const WorkspaceHistoryTab = ({ workspace, onInitiateUndo, onInitiateCancel, onInitiateRestart, onInitiateRevertToPoint, onInitiateRollbackToPlan, refreshTrigger }) => {
    const [history, setHistory] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [authState, setAuthState] = useState(authManager.getState());

    useEffect(() => {
        const unsubscribe = authManager.subscribe(setAuthState);
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (!authState.user || !workspace?.id) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError(null);
        const firestore = getFirestore();
        const historyCollectionRef = getWorkspaceSubcollectionRef(firestore, authState.user.uid, workspace.id, 'history');
        const q = query(historyCollectionRef, orderBy('createdAt', 'desc'));

        const unsubscribe = onSnapshot(q, 
            (querySnapshot) => {
                const historyData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                const processedHistory = processHistory(historyData);
                setHistory(processedHistory);
                setIsLoading(false);
            }, 
            (err) => {
                console.error("Firestore subscription error:", err);
                setError(`Could not load workspace history. Please check console.`);
                setIsLoading(false);
            }
        );

        return () => unsubscribe();
    }, [workspace.id, authState.user, refreshTrigger]);
    
    const processHistory = (historyData) => {
        let headCommitHash = null;
        for(const item of historyData) {
            const commitHash = item.endOfPhaseCommitHash || item.commitHash;
            if (item.status === 'COMPLETED' && commitHash) {
                headCommitHash = commitHash;
                break;
            }
             if(item.type === 'COMMIT' && commitHash) {
                headCommitHash = commitHash;
                break;
            }
        }

        if(!headCommitHash) return historyData.map(h => ({...h, isAncestor: true}));
        
        return historyData.map(h => {
            const itemCommitHash = h.endOfPhaseCommitHash || h.commitHash;
            return {
                ...h,
                isAncestor: (h.ancestorCommits && h.ancestorCommits.includes(headCommitHash)) || itemCommitHash === headCommitHash,
            }
        });
    }

    const timelineDividerIndex = useMemo(() => history.findIndex(h => !h.isAncestor), [history]);
    
    const displayedHistory = useMemo(() => {
        return history.filter(item => {
            const isCognitiveFailure = item.status === 'FAILED' && !item.startCommitHash;
            return !isCognitiveFailure;
        });
    }, [history]);

    if (isLoading) return <div className="h-full flex items-center justify-center"><Loader className="w-6 h-6 animate-spin text-primary" /></div>;
    if (error) return <div className="h-full flex items-center justify-center text-red-400 p-4 text-center">{error}</div>;

    return (
        <div className="h-full flex flex-col">
            <div className="p-6 pb-2 flex-shrink-0">
                <div className="flex justify-between items-start pb-4 border-b border-border-color">
                    <div>
                        <h2 className="text-lg font-bold text-on-surface">Changes</h2>
                        <p className="text-sm text-on-surface-dim mt-1">A timeline of workspace checkpoints and file changes.</p>
                    </div>
                </div>
            </div>
            <div className="flex-1 overflow-y-auto custom-scrollbar px-6">
                <div className="space-y-2 pb-6">
                    {displayedHistory.length > 0 ? (
                        displayedHistory.map((item, index) => (
                            <React.Fragment key={item.id}>
                                {index === timelineDividerIndex && timelineDividerIndex !== -1 && (
                                     <div className="flex items-center gap-3 my-4">
                                        <div className="h-px flex-1" style={{ backgroundColor: 'rgba(var(--border-color-rgb), 0.5)' }}></div>
                                        <div className="text-xs font-semibold text-on-surface-dim tracking-widest uppercase">Divergent History</div>
                                        <div className="h-px flex-1" style={{ backgroundColor: 'rgba(var(--border-color-rgb), 0.5)' }}></div>
                                    </div>
                                )}
                                <HistoryItemCard 
                                    item={item} 
                                    workspace={workspace} 
                                    onInitiateUndo={onInitiateUndo}
                                    onInitiateRevertToPoint={onInitiateRevertToPoint}
                                    onInitiateRestart={onInitiateRestart}
                                    onInitiateCancel={onInitiateCancel}
                                    onInitiateRollbackToPlan={onInitiateRollbackToPlan}
                                />
                            </React.Fragment>
                        ))
                    ) : (
                        <div className="p-8 bg-surface rounded-md text-center text-on-surface-dim">
                            No history has been recorded for this workspace yet.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default WorkspaceHistoryTab;